import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { MapPin, ShoppingBag, X, Flag, Plus, Image, Award, Lock, Edit, DollarSign, Trash2, Eye, EyeOff, MoreVertical } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { uploadProfilePicture } from '../../services/storageService';
import { changePassword } from '../../services/authService';
import SecurityUtils from '../../utils/security';
import { applyWordFilter } from '../../utils/content';
import { getReactionTotal } from '../../utils/reactions';
import Avatar from '../../components/common/Avatar';
import PostMediaGrid from '../../components/common/PostMediaGrid';
import GooglePlacesAutocomplete from '../../components/common/GooglePlacesAutocomplete';
import DateInput from '../../components/common/DateInput';
import ReportModal from '../../components/moderation/ReportModal';

const ProfileView = () => {
  const { currentUser, setCurrentUser, users, setUsers, posts, setPosts, videoFiles, setVideoFiles, imageFiles, setImageFiles, setView, viewUserProfile, wordFilters, showCreatePost, setShowCreatePost, showAlert, showConfirm } = useContext(AppContext);
  const myPosts = posts.filter(p => p.userId === currentUser.id && p.type !== 'marketplace');
  const savedPosts = posts.filter(p => p.saves?.includes(currentUser.id));
  const actualFollowers = users.filter(u => u.following?.includes(currentUser.id)).map(u => u.id);
  const myFollowerCount = actualFollowers.length;
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [showReportModal, setShowReportModal] = useState(null);
  const [activeView, setActiveView] = useState('posts'); // 'posts', 'followers', 'following', 'saved', 'bag'
  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [equipmentForm, setEquipmentForm] = useState({ category: 'driver', brand: '', model: '', number: '', notes: '' });
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [editEquipmentForm, setEditEquipmentForm] = useState({ category: 'driver', brand: '', model: '', number: '', notes: '' });
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const handleAddEquipment = async () => {
    if (!equipmentForm.brand.trim() || !equipmentForm.model.trim()) { showAlert('Please enter brand and model', 'Error'); return; }
    try {
      const newEq = await firestoreService.addEquipment(currentUser.id, {
        category: equipmentForm.category,
        brand: SecurityUtils.sanitizeText(equipmentForm.brand.trim()),
        model: SecurityUtils.sanitizeText(equipmentForm.model.trim()),
        number: SecurityUtils.sanitizeText(equipmentForm.number.trim()).slice(0, 5),
        notes: SecurityUtils.sanitizeText(equipmentForm.notes.trim())
      });
      const updatedUser = { ...currentUser, equipment: [...(currentUser.equipment || []), newEq] };
      setCurrentUser(updatedUser);
      setUsers(users.map(u => u.id === currentUser.id ? updatedUser : u));
      setShowAddEquipment(false);
      setEquipmentForm({ category: 'driver', brand: '', model: '', number: '', notes: '' });
    } catch (err) { console.error('Error adding equipment:', err); showAlert('Failed to add equipment.', 'Error'); }
  };

  const handleUpdateEquipment = async () => {
    if (!editEquipmentForm.brand.trim() || !editEquipmentForm.model.trim()) { showAlert('Please enter brand and model', 'Error'); return; }
    try {
      const updatedData = {
        category: editEquipmentForm.category,
        brand: SecurityUtils.sanitizeText(editEquipmentForm.brand.trim()),
        model: SecurityUtils.sanitizeText(editEquipmentForm.model.trim()),
        number: SecurityUtils.sanitizeText(editEquipmentForm.number.trim()).slice(0, 5),
        notes: SecurityUtils.sanitizeText(editEquipmentForm.notes.trim())
      };
      await firestoreService.updateEquipment(currentUser.id, editingEquipment.id, updatedData);
      const updatedEquipment = (currentUser.equipment || []).map(eq =>
        eq.id === editingEquipment.id ? { ...eq, ...updatedData } : eq
      );
      const updatedUser = { ...currentUser, equipment: updatedEquipment };
      setCurrentUser(updatedUser);
      setUsers(users.map(u => u.id === currentUser.id ? updatedUser : u));
      setEditingEquipment(null);
    } catch (err) { console.error('Error updating equipment:', err); showAlert('Failed to update equipment.', 'Error'); }
  };

  const handleDeleteEquipment = async (eqId) => {
    if (!await showConfirm('Remove this equipment?', 'Remove Equipment', 'Remove', true)) return;
    try {
      await firestoreService.removeEquipment(currentUser.id, eqId);
      const updatedUser = { ...currentUser, equipment: (currentUser.equipment || []).filter(eq => eq.id !== eqId) };
      setCurrentUser(updatedUser);
      setUsers(users.map(u => u.id === currentUser.id ? updatedUser : u));
    } catch (err) { console.error('Error removing equipment:', err); showAlert('Failed to remove equipment.', 'Error'); }
  };
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showPwCurrent, setShowPwCurrent] = useState(false);
  const [showPwNew, setShowPwNew] = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [profileForm, setProfileForm] = useState({
    firstName: currentUser.firstName || currentUser.name?.split(' ')[0] || '',
    lastName: currentUser.lastName || currentUser.name?.split(' ').slice(1).join(' ') || '',
    handle: currentUser.handle,
    email: currentUser.email,
    birthDate: currentUser.birthDate || '',
    gender: currentUser.gender || '',
    bio: currentUser.bio || '',
    skillLevel: currentUser.skillLevel,
    handicap: currentUser.handicap || '',
    address: currentUser.location?.address || ''
  });
  const [profileLocationData, setProfileLocationData] = useState(null);

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) { showAlert('Please select an image file', 'Error'); return; }
    if (file.size > 5 * 1024 * 1024) { showAlert('Image size should be less than 5MB', 'Error'); return; }

    try {
      const url = await uploadProfilePicture(currentUser.id, file);
      await firestoreService.updateUser(currentUser.id, { profilePicture: url });
      const updatedUser = { ...currentUser, profilePicture: url };
      setCurrentUser(updatedUser);
      setUsers(users.map(u => u.id === currentUser.id ? updatedUser : u));
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      showAlert('Failed to upload profile picture. Please try again.', 'Error');
    }
  };

  const handleRemoveProfilePicture = async () => {
    try {
      const defaultPic = `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.name)}&background=16a34a&color=fff&size=128`;
      await firestoreService.updateUser(currentUser.id, { profilePicture: defaultPic });
      const updatedUser = { ...currentUser, profilePicture: defaultPic };
      setCurrentUser(updatedUser);
      setUsers(users.map(u => u.id === currentUser.id ? updatedUser : u));
    } catch (err) {
      console.error('Error removing profile picture:', err);
    }
  };

  const handleEditProfile = () => {
    setProfileForm({
      firstName: currentUser.firstName || currentUser.name?.split(' ')[0] || '',
      lastName: currentUser.lastName || currentUser.name?.split(' ').slice(1).join(' ') || '',
      handle: currentUser.handle,
      email: currentUser.email,
      birthDate: currentUser.birthDate || '',
      gender: currentUser.gender || '',
      bio: currentUser.bio || '',
      skillLevel: currentUser.skillLevel,
      handicap: currentUser.handicap || '',
      address: currentUser.location?.address || ''
    });
    setEditingProfile(true);
  };

  const handleSaveProfile = () => {
    // Validation
    if (!profileForm.firstName.trim()) {
      showAlert('First name is required', 'Error');
      return;
    }
    if (!profileForm.lastName.trim()) {
      showAlert('Last name is required', 'Error');
      return;
    }
    if (!profileForm.handle.trim()) {
      showAlert('Handle is required', 'Error');
      return;
    }
    if (!profileForm.email.trim()) {
      showAlert('Email is required', 'Error');
      return;
    }
    if (!profileForm.birthDate) {
      showAlert('Birth date is required', 'Error');
      return;
    }
    
    // Check if handle is unique (excluding current user)
    const handleExists = users.some(u => u.id !== currentUser.id && u.handle.toLowerCase() === profileForm.handle.toLowerCase());
    if (handleExists) {
      showAlert('This handle is already taken. Please choose another one.', 'Error');
      return;
    }

    // Check if email is unique (excluding current user)
    const emailExists = users.some(u => u.id !== currentUser.id && u.email.toLowerCase() === profileForm.email.toLowerCase());
    if (emailExists) {
      showAlert('This email is already in use. Please use another one.', 'Error');
      return;
    }

    // Validate age (must be 13+)
    const birthDate = new Date(profileForm.birthDate);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
    
    if (actualAge < 13) {
      showAlert('You must be at least 13 years old to use Loft', 'Error');
      return;
    }

    // Update user
    const sanitizedFirst = SecurityUtils.sanitizeText(profileForm.firstName.trim());
    const sanitizedLast = SecurityUtils.sanitizeText(profileForm.lastName.trim());
    const updatedUser = {
      ...currentUser,
      firstName: sanitizedFirst,
      lastName: sanitizedLast,
      name: `${sanitizedFirst} ${sanitizedLast}`,
      handle: SecurityUtils.sanitizeText(profileForm.handle.trim().toLowerCase()),
      email: SecurityUtils.sanitizeText(profileForm.email.trim().toLowerCase()),
      birthDate: profileForm.birthDate,
      gender: profileForm.gender || '',
      bio: SecurityUtils.sanitizeText(profileForm.bio.trim()),
      skillLevel: profileForm.skillLevel,
      handicap: profileForm.handicap ? parseInt(profileForm.handicap) : 0,
      location: {
        address: SecurityUtils.sanitizeText(profileForm.address.trim()),
        lat: profileLocationData?.lat ?? currentUser.location?.lat ?? null,
        lon: profileLocationData?.lon ?? currentUser.location?.lon ?? null,
        zipCode: profileLocationData?.zipCode ?? currentUser.location?.zipCode ?? '',
      }
    };

    setCurrentUser(updatedUser);
    setUsers(users.map(u => u.id === currentUser.id ? updatedUser : u));
    setEditingProfile(false);
    setProfileLocationData(null);

    // Persist to Firestore
    try {
      firestoreService.updateUser(currentUser.id, {
        firstName: updatedUser.firstName,
        lastName: updatedUser.lastName,
        name: updatedUser.name,
        handle: updatedUser.handle,
        email: updatedUser.email,
        birthDate: updatedUser.birthDate,
        gender: updatedUser.gender,
        bio: updatedUser.bio,
        skillLevel: updatedUser.skillLevel,
        handicap: updatedUser.handicap,
        location: updatedUser.location,
      });
    } catch (err) {
      console.error('Error saving profile:', err);
    }
  };

  const handleCancelEdit = () => {
    setEditingProfile(false);
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleEditPost = (postId) => {
    const post = posts.find(p => p.id === postId);
    if (post) {
      setEditingPost(postId);
      setEditContent(post.content);
    }
  };

  const handleSaveEdit = (postId) => {
    if (!editContent.trim()) {
      showAlert('Post content cannot be empty', 'Error');
      return;
    }

    // Sanitize edited content to prevent XSS attacks
    const sanitizedContent = SecurityUtils.sanitizeText(editContent);

    setPosts(posts.map(p => p.id === postId ? { ...p, content: sanitizedContent, edited: true, editedAt: new Date().toISOString() } : p));
    setEditingPost(null);
    setEditContent('');
    firestoreService.updatePost(postId, { content: sanitizedContent, edited: true, editedAt: new Date().toISOString() }).catch(err => console.error('Error updating post:', err));
  };

  const handleDeletePost = (postId) => { setDeleteConfirm(postId); };

  const confirmDelete = () => {
    const postId = deleteConfirm;
    setPosts(posts.filter(p => p.id !== postId));
    setDeleteConfirm(null);
    firestoreService.deletePost(postId).catch(err => console.error('Error deleting post:', err));
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="bg-black rounded-lg shadow border border-gray-700 p-6 mb-6">
        {!editingProfile ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4 min-w-0">
                <div className="relative group flex-shrink-0">
                  <Avatar user={currentUser} size="xl" />
                  <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                    <Image className="w-6 h-6 text-white" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureUpload}
                      className="hidden"
                    />
                  </label>
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-white break-words">{currentUser.name}</h2>
                  <p className="text-sm text-white break-words">@{currentUser.handle}</p>
                </div>
              </div>
              <div className="relative flex-shrink-0 ml-2">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="p-2 text-white hover:bg-gray-800 rounded-lg"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
                {showProfileMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-lg z-10 w-48">
                    <button
                      onClick={() => { handleEditProfile(); setShowProfileMenu(false); }}
                      className="w-full px-4 py-2.5 text-left text-white hover:bg-gray-800 flex items-center space-x-2 rounded-t-lg"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Edit Profile</span>
                    </button>
                    <button
                      onClick={() => { setShowChangePassword(!showChangePassword); setPasswordMsg(null); setPasswordForm({ current: '', new: '', confirm: '' }); setShowProfileMenu(false); }}
                      className="w-full px-4 py-2.5 text-left text-white hover:bg-gray-800 flex items-center space-x-2"
                    >
                      <Lock className="w-4 h-4" />
                      <span>Change Password</span>
                    </button>
                    {currentUser.profilePicture && !currentUser.profilePicture.includes('ui-avatars.com') && (
                      <button
                        onClick={async () => { const confirmed = await showConfirm('Are you sure you want to remove your profile photo?', 'Remove Photo', 'Remove', true); if (confirmed) { handleRemoveProfilePicture(); setShowProfileMenu(false); } }}
                        className="w-full px-4 py-2.5 text-left text-red-400 hover:bg-gray-800 flex items-center space-x-2 rounded-b-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Remove Photo</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {showChangePassword && (
              <div className="bg-gray-900 rounded-lg p-4 mb-4 border border-gray-700">
                <h4 className="text-sm font-bold text-white mb-3">Change Password</h4>
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type={showPwCurrent ? 'text' : 'password'}
                      value={passwordForm.current}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                      placeholder="Current password"
                      className="w-full px-2 py-1 pr-10 border border-gray-700 rounded-lg text-white bg-black text-sm"
                    />
                    <button type="button" onClick={() => setShowPwCurrent(!showPwCurrent)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                      {showPwCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPwNew ? 'text' : 'password'}
                      value={passwordForm.new}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                      placeholder="New password"
                      className="w-full px-2 py-1 pr-10 border border-gray-700 rounded-lg text-white bg-black text-sm"
                    />
                    <button type="button" onClick={() => setShowPwNew(!showPwNew)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                      {showPwNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPwConfirm ? 'text' : 'password'}
                      value={passwordForm.confirm}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                      placeholder="Confirm new password"
                      className="w-full px-2 py-1 pr-10 border border-gray-700 rounded-lg text-white bg-black text-sm"
                    />
                    <button type="button" onClick={() => setShowPwConfirm(!showPwConfirm)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                      {showPwConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordMsg && (
                    <p className={`text-sm ${passwordMsg.type === 'error' ? 'text-red-500' : 'text-green-500'}`}>{passwordMsg.text}</p>
                  )}
                  <div className="flex space-x-2">
                    <button
                      onClick={async () => {
                        setPasswordMsg(null);
                        if (passwordForm.new.length < 8) { setPasswordMsg({ type: 'error', text: 'Password must be at least 8 characters' }); return; }
                        if (passwordForm.new !== passwordForm.confirm) { setPasswordMsg({ type: 'error', text: 'Passwords do not match' }); return; }
                        try {
                          await changePassword(passwordForm.current, passwordForm.new);
                          setPasswordMsg({ type: 'success', text: 'Password changed successfully!' });
                          setPasswordForm({ current: '', new: '', confirm: '' });
                          setTimeout(() => setShowChangePassword(false), 2000);
                        } catch (err) {
                          if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                            setPasswordMsg({ type: 'error', text: 'Current password is incorrect' });
                          } else {
                            setPasswordMsg({ type: 'error', text: 'Failed to change password. Please try again.' });
                          }
                        }
                      }}
                      className="px-3 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] text-sm"
                    >
                      Update Password
                    </button>
                    <button
                      onClick={() => { setShowChangePassword(false); setPasswordMsg(null); }}
                      className="px-3 py-1 text-white hover:bg-gray-800 rounded-lg text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* All Profile Information */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-white mb-1">First Name</p>
                  <p className="text-white">{currentUser.firstName || currentUser.name?.split(' ')[0] || ''}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-white mb-1">Last Name</p>
                  <p className="text-white">{currentUser.lastName || currentUser.name?.split(' ').slice(1).join(' ') || ''}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-white mb-1">Handle</p>
                  <p className="text-white">@{currentUser.handle}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-white mb-1">Email</p>
                  <p className="text-white">{currentUser.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-white mb-1">Age</p>
                  <p className="text-white">
                    {currentUser.birthDate ? `${calculateAge(currentUser.birthDate)} years old` : 'Not set'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-white mb-1">Gender</p>
                  <p className="text-white">{currentUser.gender || 'Prefer not to say'}</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-white mb-1">Bio</p>
                <p className="text-white">{currentUser.bio || 'No bio added yet'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-white mb-1">Skill Level</p>
                  <p className="text-white">{currentUser.skillLevel}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-white mb-1">Handicap</p>
                  <p className="text-white">{currentUser.handicap || 'Not set'}</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-white mb-1">Location</p>
                <p className="text-white flex items-center">
                  <MapPin className="w-4 h-4 mr-1 text-white" />
                  {currentUser.location?.address || 'Unknown'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 pt-4 border-t mt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{currentUser.handicap || '-'}</p>
                <p className="text-sm text-white">Handicap</p>
              </div>
              <button onClick={() => setActiveView('followers')} className="text-center hover:bg-gray-900 rounded-lg p-2 transition-colors">
                <p className="text-2xl font-bold text-white">{myFollowerCount}</p>
                <p className="text-sm text-white">Followers</p>
              </button>
              <button onClick={() => setActiveView('following')} className="text-center hover:bg-gray-900 rounded-lg p-2 transition-colors">
                <p className="text-2xl font-bold text-white">{currentUser.following.length}</p>
                <p className="text-sm text-white">Following</p>
              </button>
              <button onClick={() => setActiveView('saved')} className="text-center hover:bg-gray-900 rounded-lg p-2 transition-colors">
                <p className="text-2xl font-bold text-white">{savedPosts.length}</p>
                <p className="text-sm text-white">Saved</p>
              </button>
            </div>

            <div className="pt-4 border-t mt-4 text-xs text-white">
              <p>Joined {new Date(currentUser.joinDate).toLocaleDateString()}</p>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Edit Profile</h3>
              <button
                onClick={handleCancelEdit}
                className="text-white hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Profile Picture Upload Section */}
            <div className="mb-6 pb-6 border-b border-gray-800">
              <label className="block text-sm font-medium text-white mb-3">Profile Picture</label>
              <div className="flex items-center space-x-4">
                <Avatar user={currentUser} size="lg" />
                <div className="flex flex-col space-y-2">
                  <label className="cursor-pointer">
                    <span className="px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] inline-flex items-center space-x-2 text-sm">
                      <Image className="w-4 h-4" />
                      <span>Upload Photo</span>
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureUpload}
                      className="hidden"
                    />
                  </label>
                  <p className="text-xs text-white">Max size: 5MB. Accepts: JPG, PNG, GIF</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-white mb-1">First Name *</label>
                <input
                  type="text"
                  value={profileForm.firstName}
                  onChange={(e) => setProfileForm({...profileForm, firstName: e.target.value})}
                  className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Last Name *</label>
                <input
                  type="text"
                  value={profileForm.lastName}
                  onChange={(e) => setProfileForm({...profileForm, lastName: e.target.value})}
                  className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">Handle *</label>
              <div className="relative">
                <span className="absolute left-4 top-2 text-white">@</span>
                <input
                  type="text"
                  value={profileForm.handle}
                  onChange={(e) => setProfileForm({...profileForm, handle: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')})}
                  className="w-full pl-8 pr-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                  placeholder="yourhandle"
                />
              </div>
              <p className="text-xs text-white mt-1">Only lowercase letters, numbers, and underscores</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">Email *</label>
              <input
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">Birth Date *</label>
              <DateInput
                value={profileForm.birthDate}
                onChange={(v) => setProfileForm({...profileForm, birthDate: v})}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
              />
              <p className="text-xs text-white mt-1">You must be at least 13 years old</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">Gender</label>
              <select
                value={profileForm.gender}
                onChange={(e) => setProfileForm({...profileForm, gender: e.target.value})}
                className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
              >
                <option value="">Prefer not to say</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Non-binary">Non-binary</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">Bio</label>
              <textarea
                value={profileForm.bio}
                onChange={(e) => setProfileForm({...profileForm, bio: e.target.value})}
                className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] resize-none text-white bg-black"
                rows="3"
                placeholder="Tell us about yourself..."
                maxLength={160}
              />
              <p className="text-xs text-white mt-1">{profileForm.bio.length}/160 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">Skill Level *</label>
              <select
                value={profileForm.skillLevel}
                onChange={(e) => setProfileForm({...profileForm, skillLevel: e.target.value})}
                className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Professional">Professional</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">Handicap</label>
              <input
                type="number"
                value={profileForm.handicap}
                onChange={(e) => setProfileForm({...profileForm, handicap: e.target.value})}
                className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                placeholder="Your handicap"
                min="0"
                max="54"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">Location *</label>
              <GooglePlacesAutocomplete
                value={profileForm.address}
                onChange={(value) => setProfileForm({...profileForm, address: value})}
                onSelect={(locData) => setProfileLocationData(locData)}
                placeholder="Start typing your address..."
                className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                required={true}
              />
              <p className="text-xs text-white mt-1">
                Type your address and select from suggestions
              </p>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={handleCancelEdit}
                className="flex-1 px-2 py-1 border border-gray-700 text-white rounded-lg hover:bg-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProfile}
                className="flex-1 px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]"
              >
                Save Changes
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Tabs */}
      <div className="flex space-x-2 mb-4">
        <button
          onClick={() => setActiveView('posts')}
          className={`px-2 py-1 rounded-lg font-medium transition-colors ${activeView === 'posts' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
        >
          My Posts ({myPosts.length})
        </button>
        <button
          onClick={() => setActiveView('followers')}
          className={`px-2 py-1 rounded-lg font-medium transition-colors ${activeView === 'followers' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
        >
          Followers ({myFollowerCount})
        </button>
        <button
          onClick={() => setActiveView('following')}
          className={`px-2 py-1 rounded-lg font-medium transition-colors ${activeView === 'following' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
        >
          Following ({currentUser.following.length})
        </button>
        <button
          onClick={() => setActiveView('saved')}
          className={`px-2 py-1 rounded-lg font-medium transition-colors ${activeView === 'saved' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
        >
          Saved ({savedPosts.length})
        </button>
        <button
          onClick={() => setActiveView('bag')}
          className={`px-2 py-1 rounded-lg font-medium transition-colors ${activeView === 'bag' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
        >
          My Bag ({(currentUser.equipment || []).length})
        </button>
      </div>

      {/* Posts View */}
      {activeView === 'posts' && (
        <div className="space-y-4">
          {myPosts.map(post => {
          const isEditing = editingPost === post.id;
          
          return (
            <div key={post.id} className="bg-black rounded-lg shadow border border-gray-700 p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-white">
                  {new Date(post.timestamp).toLocaleDateString()}
                  {post.edited && <span className="ml-2 text-white">(edited)</span>}
                </p>
                {!isEditing && (
                  <div className="flex items-center space-x-2">
                    <button onClick={() => handleEditPost(post.id)} className="text-white hover:text-blue-600">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeletePost(post.id)} className="text-white hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {isEditing ? (
                <div className="mb-3">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] resize-none text-white bg-black"
                    rows="3"
                  />
                  <div className="mt-2 flex justify-end space-x-2">
                    <button onClick={() => { setEditingPost(null); setEditContent(''); }} className="px-3 py-1 text-white hover:bg-gray-800 rounded-lg text-sm">
                      Cancel
                    </button>
                    <button onClick={() => handleSaveEdit(post.id)} className="px-3 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] text-sm">
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-white mb-2">{applyWordFilter(post.content, wordFilters)}</p>
              )}

              <PostMediaGrid post={post} />

              <div className="flex items-center space-x-4 text-sm text-white">
                <span>{getReactionTotal(post)} reactions</span>
                <span>{post.comments.length} comments</span>
              </div>
            </div>
          );
        })}
        </div>
      )}

      {/* Followers View */}
      {activeView === 'followers' && (
        <div className="space-y-4">
          {actualFollowers.length === 0 ? (
            <div className="bg-black rounded-lg shadow border border-gray-700 p-8 text-center">
              <p className="text-white">No followers yet</p>
            </div>
          ) : (
            actualFollowers.map(followerId => {
              const follower = users.find(u => u.id === followerId);
              if (!follower) return null;
              return (
                <div key={follower.id} className="bg-black rounded-lg shadow border border-gray-700 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Avatar user={follower} size="md" />
                      <div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => viewUserProfile(follower.id)}
                            className="font-bold text-white hover:text-[#16a34a]"
                          >
                            {follower.name}
                          </button>
                          {follower.isCoach && (
                            <span className="px-2 py-0.5 bg-blue-800 text-pure-white text-xs rounded-full flex items-center">
                              <Award className="w-3 h-3 mr-1" />
                              Coach
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-white">@{follower.handle}</p>
                        {follower.bio && <p className="text-sm text-white mt-1">{follower.bio}</p>}
                      </div>
                    </div>
                    <button
                      onClick={() => viewUserProfile(follower.id)}
                      className="px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] text-sm"
                    >
                      View Profile
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Following View */}
      {activeView === 'following' && (
        <div className="space-y-4">
          {currentUser.following.length === 0 ? (
            <div className="bg-black rounded-lg shadow border border-gray-700 p-8 text-center">
              <p className="text-white">Not following anyone yet</p>
            </div>
          ) : (
            currentUser.following.map(followingId => {
              const followedUser = users.find(u => u.id === followingId);
              if (!followedUser) return null;
              return (
                <div key={followedUser.id} className="bg-black rounded-lg shadow border border-gray-700 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Avatar user={followedUser} size="md" />
                      <div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => viewUserProfile(followedUser.id)}
                            className="font-bold text-white hover:text-[#16a34a]"
                          >
                            {followedUser.name}
                          </button>
                          {followedUser.isCoach && (
                            <span className="px-2 py-0.5 bg-blue-800 text-pure-white text-xs rounded-full flex items-center">
                              <Award className="w-3 h-3 mr-1" />
                              Coach
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-white">@{followedUser.handle}</p>
                        {followedUser.bio && <p className="text-sm text-white mt-1">{followedUser.bio}</p>}
                      </div>
                    </div>
                    <button
                      onClick={() => viewUserProfile(followedUser.id)}
                      className="px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] text-sm"
                    >
                      View Profile
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Saved Posts View */}
      {activeView === 'saved' && (
        <div className="space-y-4">
          {savedPosts.length === 0 ? (
            <div className="bg-black rounded-lg shadow border border-gray-700 p-8 text-center">
              <p className="text-white">No saved posts yet</p>
            </div>
          ) : (
            savedPosts.map(post => {
              const author = users.find(u => u.id === post.userId);
              if (!author) return null;

              return (
                <div key={post.id} className="bg-black rounded-lg shadow border border-gray-700 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <Avatar user={author} size="sm" />
                      <div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => viewUserProfile(author.id)}
                            className="font-bold text-white hover:text-[#16a34a]"
                          >
                            {author.name}
                          </button>
                          {author.isCoach && (
                            <span className="px-2 py-0.5 bg-blue-800 text-pure-white text-xs rounded-full flex items-center">
                              <Award className="w-3 h-3 mr-1" />
                              Coach
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-white">@{author.handle} · {new Date(post.timestamp).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowReportModal({ type: 'post', targetId: post.id, targetUserId: post.userId })}
                      className="text-white hover:text-red-600"
                      title="Report post"
                    >
                      <Flag className="w-4 h-4" />
                    </button>
                  </div>

                  <p className="text-white mb-2">{applyWordFilter(post.content, wordFilters)}</p>

                  <PostMediaGrid post={post} />

                  <div className="flex items-center space-x-4 text-sm text-white">
                    <span>{getReactionTotal(post)} reactions</span>
                    <span>{post.comments.length} comments</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* My Bag View */}
      {activeView === 'bag' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white flex items-center">
              <ShoppingBag className="w-5 h-5 mr-2" />
              My Bag
            </h3>
            <button onClick={() => setShowAddEquipment(true)}
              className="px-3 py-1 bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d] flex items-center space-x-1 text-sm">
              <Plus className="w-4 h-4" /><span>Add Club</span>
            </button>
          </div>

          {(!currentUser.equipment || currentUser.equipment.length === 0) ? (
            <div className="bg-black rounded-lg border border-gray-700 p-8 text-center">
              <ShoppingBag className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-400 mb-1">No equipment added yet</p>
              <p className="text-sm text-gray-500">Add your clubs to showcase your bag!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {['driver', 'woods', 'hybrids', 'irons', 'wedges', 'putter', 'ball', 'other'].map(cat => {
                const items = (currentUser.equipment || []).filter(eq => eq.category === cat);
                if (items.length === 0) return null;
                return (
                  <div key={cat} className="bg-black rounded-lg border border-gray-700 p-4">
                    <h4 className="text-sm font-bold text-gray-400 mb-2 capitalize">{cat}</h4>
                    <div className="space-y-2">
                      {items.map(eq => (
                        <div key={eq.id} className="bg-gray-900 rounded-lg p-3 flex items-start justify-between">
                          <div>
                            <p className="text-white font-medium">{eq.brand} {eq.model}{eq.number ? ` (${eq.number})` : ''}</p>
                            {eq.notes && <p className="text-sm text-gray-400 mt-1">{eq.notes}</p>}
                          </div>
                          <div className="flex space-x-2 ml-3">
                            <button onClick={() => { setEditingEquipment(eq); setEditEquipmentForm({ category: eq.category, brand: eq.brand, model: eq.model, number: eq.number || '', notes: eq.notes || '' }); }}
                              className="text-gray-400 hover:text-white" title="Edit">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={() => { setView('marketplace'); setShowCreatePost(true); }}
                              className="text-[#16a34a] hover:text-[#15803d]" title="Sell on Marketplace">
                              <DollarSign className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteEquipment(eq.id)}
                              className="text-gray-500 hover:text-red-500" title="Remove">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Edit Equipment Modal */}
          {editingEquipment && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
              <div className="bg-black border border-gray-700 rounded-lg max-w-md w-full">
                <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">Edit Equipment</h3>
                  <button onClick={() => setEditingEquipment(null)} className="text-gray-400 hover:text-white"><X className="w-6 h-6" /></button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Category</label>
                    <select value={editEquipmentForm.category} onChange={(e) => setEditEquipmentForm({ ...editEquipmentForm, category: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black">
                      <option value="driver">Driver</option>
                      <option value="woods">Woods</option>
                      <option value="hybrids">Hybrids</option>
                      <option value="irons">Irons</option>
                      <option value="wedges">Wedges</option>
                      <option value="putter">Putter</option>
                      <option value="ball">Ball</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Brand *</label>
                    <input type="text" value={editEquipmentForm.brand} onChange={(e) => setEditEquipmentForm({ ...editEquipmentForm, brand: e.target.value })}
                      placeholder="e.g., TaylorMade, Titleist" className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Model *</label>
                    <input type="text" value={editEquipmentForm.model} onChange={(e) => setEditEquipmentForm({ ...editEquipmentForm, model: e.target.value })}
                      placeholder="e.g., Stealth 2, Pro V1" className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Number (Optional)</label>
                    <input type="text" value={editEquipmentForm.number}
                      onChange={(e) => setEditEquipmentForm({ ...editEquipmentForm, number: e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 5) })}
                      placeholder="e.g., 7, PW, SW" maxLength={5}
                      className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Notes (Optional)</label>
                    <textarea value={editEquipmentForm.notes} onChange={(e) => setEditEquipmentForm({ ...editEquipmentForm, notes: e.target.value })}
                      placeholder="Loft, shaft, or any other details..." className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black" rows="3" />
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button onClick={() => setEditingEquipment(null)} className="flex-1 px-2 py-1 border border-gray-700 text-white rounded-lg hover:bg-gray-900">Cancel</button>
                    <button onClick={handleUpdateEquipment} className="flex-1 px-2 py-1 bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]">Save Changes</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add Equipment Modal */}
          {showAddEquipment && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
              <div className="bg-black border border-gray-700 rounded-lg max-w-md w-full">
                <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">Add Equipment</h3>
                  <button onClick={() => setShowAddEquipment(false)} className="text-gray-400 hover:text-white"><X className="w-6 h-6" /></button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Category</label>
                    <select value={equipmentForm.category} onChange={(e) => setEquipmentForm({ ...equipmentForm, category: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black">
                      <option value="driver">Driver</option>
                      <option value="woods">Woods</option>
                      <option value="hybrids">Hybrids</option>
                      <option value="irons">Irons</option>
                      <option value="wedges">Wedges</option>
                      <option value="putter">Putter</option>
                      <option value="ball">Ball</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Brand *</label>
                    <input type="text" value={equipmentForm.brand} onChange={(e) => setEquipmentForm({ ...equipmentForm, brand: e.target.value })}
                      placeholder="e.g., TaylorMade, Titleist" className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Model *</label>
                    <input type="text" value={equipmentForm.model} onChange={(e) => setEquipmentForm({ ...equipmentForm, model: e.target.value })}
                      placeholder="e.g., Stealth 2, Pro V1" className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Number (Optional)</label>
                    <input type="text" value={equipmentForm.number}
                      onChange={(e) => setEquipmentForm({ ...equipmentForm, number: e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 5) })}
                      placeholder="e.g., 7, PW, SW" maxLength={5}
                      className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Notes (Optional)</label>
                    <textarea value={equipmentForm.notes} onChange={(e) => setEquipmentForm({ ...equipmentForm, notes: e.target.value })}
                      placeholder="Loft, shaft, or any other details..." className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black" rows="3" />
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button onClick={() => setShowAddEquipment(false)} className="flex-1 px-2 py-1 border border-gray-700 text-white rounded-lg hover:bg-gray-900">Cancel</button>
                    <button onClick={handleAddEquipment} className="flex-1 px-2 py-1 bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]">Add Equipment</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-black rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-bold text-white mb-2">Delete Post?</h3>
            <p className="text-white mb-6">Are you sure you want to delete this post? This action cannot be undone.</p>
            <div className="flex space-x-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-2 py-1 border border-gray-700 text-white rounded-lg hover:bg-gray-900">
                Cancel
              </button>
              <button onClick={confirmDelete} className="flex-1 px-2 py-1 bg-red-600 text-pure-white rounded-lg hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showReportModal && (
        <ReportModal
          onClose={() => setShowReportModal(null)}
          reportType={showReportModal.type}
          targetId={showReportModal.targetId}
          targetUserId={showReportModal.targetUserId}
        />
      )}
    </div>
  );
};

export default ProfileView;
